/**
 * @file poolVerificationApi.js
 * @description Utility module for handling pool verification operations.
 *              Includes functions to fetch, update pool verification status, 
 *              and retrieve detailed pool verification information.
 * @created 
 * @author Govind Dindewad
 */

import axios from "axios";
import apiUrl from "./api";

// -------------------- POOL VERIFICATION CRUD OPERATIONS --------------------

/**
 * Fetches a list of pool verifications based on various filters.
 * @param {Object} params - Parameters for the request.
 * @param {string} [params.key=''] - Optional search key for filtering pool verifications.
 * @param {string} [params.type='All'] - Type of pool verification (e.g., 'All', 'Pending').
 * @param {string} [params.status='All'] - Status of the verification (e.g., 'All', 'Approved').
 * @param {number} [params.page=0] - Page number for pagination (default is 0).
 * @returns {Promise<Object>} - Axios response containing a list of pool verifications or error response.
 */
async function getPoolVerificationList({ key = '', type = 'All', status = 'All', page = 0 }) {
    const token = window.localStorage.getItem('token');
    
    try {
        const response = await axios.get(`${apiUrl}secure/verification/retain-all-pool-verifications?key=${key}&type=${type}&status=${status}&page=${page}&size=50`, {
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        });
        return response;

    } catch (error) {
if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

/**
 * Retrieves detailed information for a specific pool verification.
 * @param {Object} pool - Pool object that contains the poolId.
 * @param {string} pool.poolId - The unique ID of the pool for which verification details are being fetched.
 * @returns {Promise<Object>} - Axios response containing pool verification details or error response.
 */
async function getPoolVerificationDetails(pool) {
    const token = window.localStorage.getItem('token');
    
    try {
        const response = await axios.get(`${apiUrl}secure/verification/${pool.poolId}/retain`, {
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        });
        return response;

    } catch (error) {
if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

/**
 * Updates the verification status for a pool, including a reason if applicable.
 * @param {string} poolId - The ID of the pool whose verification status is being updated.
 * @param {string} status - The new verification status (e.g., 'Approved', 'Rejected').
 * @param {string} reason - The reason for changing the verification status (if applicable).
 * @returns {Promise<Object>} - Axios response confirming the status update or error response.
 */
async function updateVerificationStatus(poolId, status, reason) {
    const token = window.localStorage.getItem('token');
    const userData = JSON.parse(window.localStorage.getItem('userData'));

    const data = {
        poolId: poolId,
        verificationStatus: status,
        verificationFailedDescription: reason
    }

    try {
        const response = await axios.put(`${apiUrl}secure/verification/update-verification-status`, data, {
            headers: {
                "X-User-Id": userData.userId,
                'Authorization': `Bearer ${token}`,
            }
        });
        return response;

    } catch (error) {
   if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error;
    }
}

// Exporting all functions to be used in other modules.
export { getPoolVerificationList, getPoolVerificationDetails, updateVerificationStatus };
